/**
 * VideoSummarizeModal.js - Video Summarize Modal
 * Modal for uploading a video file OR pasting a URL from social platforms
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Icons } from './Icons';
import { config } from '../config';

/**
 * Format file size in human-readable form
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size string
 */
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Validate the selected video file against configured limits
 * @param {File} file - The file to validate
 * @returns {{ valid: boolean, error: string|null }}
 */
const validateVideoFile = (file) => {
  if (!file) {
    return { valid: false, error: 'No file selected.' };
  }

  const fileName = file.name.toLowerCase();
  const allowedFormats = config.videoAllowedFormats;
  const hasValidExtension = allowedFormats.some(fmt => fileName.endsWith(fmt));
  if (!hasValidExtension) {
    return {
      valid: false,
      error: `Unsupported format. Allowed: ${allowedFormats.join(', ')}`
    };
  }

  const maxSizeBytes = config.videoMaxFileSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File too large (${formatFileSize(file.size)}). Maximum: ${config.videoMaxFileSizeMB} MB.`
    };
  }

  return { valid: true, error: null };
};

/**
 * Basic URL validation
 * @param {string} url
 * @returns {{ valid: boolean, error: string|null }}
 */
const validateUrl = (url) => {
  if (!url || !url.trim()) {
    return { valid: false, error: 'Please enter a video URL.' };
  }
  try {
    const parsed = new URL(url.trim());
    if (!parsed.protocol.startsWith('http')) {
      return { valid: false, error: 'URL must start with http:// or https://' };
    }
  } catch {
    return { valid: false, error: 'Please enter a valid URL.' };
  }
  return { valid: true, error: null };
};

const TAB_UPLOAD = 'upload';
const TAB_URL = 'url';

function VideoSummarizeModal({
  isOpen,
  onClose,
  onSubmitFile,
  onSubmitUrl,
  onTranscribeFile,
  onTranscribeUrl,
  isLoading,
  initialValues,
  mode = 'summarize',
}) {
  const [activeTab, setActiveTab] = useState(TAB_UPLOAD);
  const [selectedFile, setSelectedFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [userPrompt, setUserPrompt] = useState('');
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const isTranscribeMode = mode === 'transcribe';

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setVideoUrl('');
    setUserPrompt('');
    setError('');
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const initial = initialValues || {};
    setError('');
    setIsDragging(false);
    setUserPrompt(initial.prompt || '');
    if (initial.url) {
      setVideoUrl(initial.url);
      setActiveTab(TAB_URL);
      setSelectedFile(null);
    } else if (initial.initialFile) {
      setSelectedFile(initial.initialFile);
      setActiveTab(TAB_UPLOAD);
      setVideoUrl('');
    } else {
      setActiveTab(TAB_UPLOAD);
      setVideoUrl('');
      setSelectedFile(null);
    }
  }, [isOpen, initialValues]);

  if (!isOpen) return null;

  const handleTabChange = (tab) => {
    if (isLoading) return;
    setActiveTab(tab);
    setError('');
  };

  // --- File upload handlers ---
  const handleFileSelect = (file) => {
    setError('');
    const validation = validateVideoFile(file);
    if (!validation.valid) {
      setError(validation.error);
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // --- Submit ---
  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (activeTab === TAB_UPLOAD) {
      if (!selectedFile) {
        setError('Please select a video file.');
        return;
      }
      const validation = validateVideoFile(selectedFile);
      if (!validation.valid) {
        setError(validation.error);
        return;
      }
      if (isTranscribeMode) {
        onTranscribeFile(selectedFile, null);
      } else {
        onSubmitFile(selectedFile, userPrompt.trim());
      }
    } else {
      const validation = validateUrl(videoUrl);
      if (!validation.valid) {
        setError(validation.error);
        return;
      }
      if (isTranscribeMode) {
        onTranscribeUrl(videoUrl.trim(), null);
      } else {
        onSubmitUrl(videoUrl.trim(), userPrompt.trim());
      }
    }
  };

  // --- Close ---
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      resetState();
      onClose();
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      resetState();
      onClose();
    }
  };

  const isSubmitDisabled = isLoading || (
    activeTab === TAB_UPLOAD ? !selectedFile : !videoUrl.trim()
  );

  return (
    <div className="video-modal-overlay" onClick={handleOverlayClick}>
      <div className="video-modal">
        <div className="video-modal-header">
          <div className="video-modal-title">
            <Icons.Video />
            <h2>{isTranscribeMode ? 'Transcribe Video' : 'Analyze Video'}</h2>
          </div>
          <button
            className="video-modal-close"
            onClick={handleClose}
            disabled={isLoading}
            type="button"
          >
            <Icons.Close />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="video-modal-content">
          {/* Tab Selector */}
          <div className="video-tabs">
            <button
              type="button"
              className={`video-tab ${activeTab === TAB_UPLOAD ? 'active' : ''}`}
              onClick={() => handleTabChange(TAB_UPLOAD)}
              disabled={isLoading}
            >
              Upload File
            </button>
            <button
              type="button"
              className={`video-tab ${activeTab === TAB_URL ? 'active' : ''}`}
              onClick={() => handleTabChange(TAB_URL)}
              disabled={isLoading}
            >
              Video URL
            </button>
          </div>

          {/* Upload File Tab */}
          {activeTab === TAB_UPLOAD && (
            <div className="video-upload-section">
              <p className="video-upload-hint">
                Supported formats: {config.videoAllowedFormats.join(', ')} | Max size: {config.videoMaxFileSizeMB} MB
              </p>

              {!selectedFile ? (
                <div
                  className={`video-drop-zone ${isDragging ? 'dragging' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => !isLoading && fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && !isLoading && fileInputRef.current?.click()}
                >
                  <Icons.Video />
                  <span className="video-drop-zone-text">
                    {isDragging ? 'Drop video here' : 'Drag & drop a video file or click to browse'}
                  </span>
                </div>
              ) : (
                <div className="video-file-info">
                  <div className="video-file-details">
                    <Icons.Video />
                    <div className="video-file-text">
                      <span className="video-file-name">{selectedFile.name}</span>
                      <span className="video-file-size">{formatFileSize(selectedFile.size)}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="video-file-remove"
                    onClick={handleRemoveFile}
                    disabled={isLoading}
                    title="Remove file"
                  >
                    <Icons.Close />
                  </button>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept={config.videoAllowedFormats.join(',')}
                onChange={handleInputChange}
                style={{ display: 'none' }}
                disabled={isLoading}
              />
            </div>
          )}

          {/* Video URL Tab */}
          {activeTab === TAB_URL && (
            <div className="video-url-section">
              <p className="video-upload-hint">
                Supported platforms: {config.videoSupportedPlatforms}
              </p>
              <input
                type="text"
                className="video-url-input"
                value={videoUrl}
                onChange={(e) => { setVideoUrl(e.target.value); setError(''); }}
                placeholder="https://www.youtube.com/watch?v=..."
                disabled={isLoading}
                autoFocus
                autoComplete="off"
              />
            </div>
          )}

          {!isTranscribeMode && (
            <div className="video-prompt-section">
              <label className="video-prompt-label" htmlFor="video-user-prompt">
                Instructions (optional)
              </label>
              <textarea
                id="video-user-prompt"
                className="video-prompt-input"
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                placeholder="What would you like to know about this video? (e.g., translate to English, summarize key points, list all people shown...). Leave empty for a general summary."
                disabled={isLoading}
                rows={3}
              />
            </div>
          )}

          {error && (
            <div className="video-error">
              {error}
            </div>
          )}

          <div className="video-modal-actions">
            <button
              type="button"
              className="video-btn-cancel"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="video-btn-submit"
              disabled={isSubmitDisabled}
            >
              {isLoading ? (
                <>
                  <span className="video-loading-spinner"></span>
                  {isTranscribeMode
                    ? (activeTab === TAB_URL ? 'Downloading & Transcribing...' : 'Transcribing...')
                    : (activeTab === TAB_URL ? 'Downloading & Analyzing...' : 'Analyzing...')}
                </>
              ) : (
                <>
                  <Icons.Video />
                  {isTranscribeMode ? 'Transcribe' : 'Analyze'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default VideoSummarizeModal;
